import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { makeStyles } from "@material-ui/core/styles"

import FormControl from "@material-ui/core/FormControl"
import TextField from "@material-ui/core/TextField"
import InputAdornment from "@material-ui/core/InputAdornment"
import InputLabel from "@material-ui/core/InputLabel"
import Select from "@material-ui/core/Select"
import Box from "@material-ui/core/Box"
import FormHelperText from "@material-ui/core/FormHelperText"
import Typography from "@material-ui/core/Typography"
import Checkbox from "@material-ui/core/Checkbox"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import ListSubheader from "@material-ui/core/ListSubheader"
import CircularProgress from "@material-ui/core/CircularProgress"

import BusinessIcon from "@material-ui/icons/Business"
import GroupIcon from "@material-ui/icons/Group"
import PermMediaIcon from "@material-ui/icons/PermMedia"

import AvatarInput from "components/partials/AvatarInput"
import Chip from "../Chip"
import { sortUsers } from "common/helpers/admin-page-func.helper"
import { HelpTooltip, UserRoleChip } from "components/widgets"
import { UsersApi, CatalogsApi } from "common/api"
import { NumberTextField, UserAvatar, MenuItem } from "components/partials"

const useStyles = makeStyles(theme => ({
	formControl: {
		display: "grid",
		gridTemplateColumns: "1fr 1fr",
		gap: theme.spacing(4),
		alignItems: "center",

		// Adjustment to align checkbox with text input
		"& .MuiFormControlLabel-root": {
			position: "relative",
			top: 8
		}
	},
	checkbox: {
		marginRight: 0,
		"& .MuiFormControlLabel-label": {
			flexGrow: 1
		}
	}
}))

const Form = props => {
	const { formData, error, onFormChange, formProps } = props
	const { t } = useTranslation()

	const classes = useStyles()

	const [users, setUsers] = useState(null)
	const groupedUsers = useGroupedUsers(users || [], formData)

	const [catalogs, setCatalogs] = useState(null)
	const groupedCatalogs = useGroupedCatalogs(catalogs || [], formData)

	useEffect(() => {
		UsersApi.get().then(response => {
			setUsers(response.data)
		})
	}, [])

	useEffect(() => {
		CatalogsApi.get().then(response => {
			setCatalogs(response.data)
		})
	}, [])

	const onChange = key => event => {
		if (event.target.type === "checkbox") {
			onFormChange(key, event.target.checked)
		} else {
			onFormChange(key, event.target.value)
		}
	}

	function onChangeProfile(base64Image) {
		onFormChange("profileImage", base64Image)
	}

	// For some reason, there's a bug when we use `formData`
	// properties for select values.
	const userIds = formData.users?.map(({ id }) => id) || []
	const catalogIds = formData.catalogs?.map(({ id }) => id) || []
	const usersValue = users?.filter(({ id }) => userIds.includes(id))
	const catalogsValue = catalogs?.filter(({ id }) => catalogIds.includes(id))

	const isUnlimitedUserLicenses = Boolean(formData.unlimitedUserLicenses)
	const isUnlimitedCatalogLicenses = Boolean(formData.unlimitedCatalogLicenses)

	const renderGroupedUsers = groupName => {
		const items = groupedUsers[groupName].map(user => (
			<MenuItem key={user.id} value={user}>
				<UserMenuItem user={user} />
			</MenuItem>
		))

		if (!items.length) {
			return null
		}

		return [<ListSubheader disableSticky>{groupName}</ListSubheader>, items]
	}

	const renderGroupedCatalogs = groupName => {
		const items = groupedCatalogs[groupName].map(catalog => {
			const isSelected = catalogsValue?.some(
				selected => selected.id === catalog.id
			)
			const shouldBeDisabled = catalog?.company?.id === formData.id

			return (
				<MenuItem
					key={catalog.id}
					value={catalog}
					disabled={isSelected && shouldBeDisabled}
				>
					{catalog.name}
				</MenuItem>
			)
		})

		if (!items.length) {
			return null
		}

		return [<ListSubheader disableSticky>{groupName}</ListSubheader>, items]
	}

	return (
		<form {...formProps}>
			<FormControl fullWidth style={{ display: "flex", alignItems: "center" }}>
				<AvatarInput
					onChange={onChangeProfile}
					AvatarProps={{
						src: formData?.profileImage || undefined
					}}
				/>
			</FormControl>

			<FormControl fullWidth className={classes.formControl}>
				<Box display="flex" alignItems="center">
					<TextField
						required
						fullWidth
						label={t("view.admin.name")}
						value={formData.name || ""}
						onChange={onChange("name")}
						error={Boolean(error?.name)}
						helperText={error?.name}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<BusinessIcon />
								</InputAdornment>
							)
						}}
					/>
					<HelpTooltip
						title={t("view.adminCompanies.nameTooltip")}
						IconProps={{ style: { marginTop: 13, marginLeft: 8 } }}
					/>
				</Box>

				<FormControlLabel
					className={classes.checkbox}
					control={
						<Checkbox
							name="enabled"
							color="primary"
							checked={Boolean(formData.enabled)}
							onChange={onChange("enabled")}
						/>
					}
					label={
						<Box display="flex">
							<Typography>
								{t("view.adminCompanies.companyEnabledLabel")}
							</Typography>
							<HelpTooltip
								title={t("view.adminCompanies.companyEnabledTooltip")}
								IconProps={{ style: { marginLeft: "auto" } }}
							/>
						</Box>
					}
				/>
			</FormControl>

			<FormControl fullWidth margin="normal" className={classes.formControl}>
				<Box display="flex" alignItems="center">
					<NumberTextField
						fullWidth
						required={!formData.unlimitedUserLicenses}
						label={t("view.adminCompanies.userLicensesLabel")}
						value={
							formData.userLicenses === undefined ? "" : formData.userLicenses
						}
						onChange={onChange("userLicenses")}
						error={Boolean(error?.userLicenses)}
						helperText={error?.userLicenses}
						InputProps={{
							readOnly: isUnlimitedUserLicenses,
							disabled: isUnlimitedUserLicenses,
							endAdornment: (
								<InputAdornment position="end">
									<GroupIcon />
								</InputAdornment>
							)
						}}
					/>
					<HelpTooltip
						title={t("view.adminCompanies.userLicensesTooltip")}
						IconProps={{ style: { marginTop: 13, marginLeft: 8 } }}
					/>
				</Box>

				<FormControlLabel
					className={classes.checkbox}
					control={
						<Checkbox
							name="verified"
							color="primary"
							checked={isUnlimitedUserLicenses}
							onChange={onChange("unlimitedUserLicenses")}
						/>
					}
					label={
						<Box display="flex">
							<Typography>
								{t("view.adminCompanies.unlimitedUserLicenseLabel")}
							</Typography>
							<HelpTooltip
								title={t("view.adminCompanies.unlimitedUserLicensesTooltip")}
								IconProps={{ style: { marginLeft: "auto" } }}
							/>
						</Box>
					}
				/>
			</FormControl>

			<FormControl fullWidth margin="normal" className={classes.formControl}>
				<Box display="flex" alignItems="center">
					<NumberTextField
						fullWidth
						required={!formData.unlimitedCatalogLicenses}
						label={t("view.adminCompanies.catalogLicensesLabel")}
						value={
							formData.catalogLicenses === undefined
								? ""
								: formData.catalogLicenses
						}
						onChange={onChange("catalogLicenses")}
						error={Boolean(error?.catalogLicenses)}
						helperText={error?.catalogLicenses}
						InputProps={{
							readOnly: isUnlimitedCatalogLicenses,
							disabled: isUnlimitedCatalogLicenses,
							endAdornment: (
								<InputAdornment position="end">
									<PermMediaIcon />
								</InputAdornment>
							)
						}}
					/>
					<HelpTooltip
						title={t("view.adminCompanies.catalogLicensesTooltip")}
						IconProps={{ style: { marginTop: 13, marginLeft: 8 } }}
					/>
				</Box>

				<FormControlLabel
					className={classes.checkbox}
					control={
						<Checkbox
							name="verified"
							color="primary"
							checked={isUnlimitedCatalogLicenses}
							onChange={onChange("unlimitedCatalogLicenses")}
						/>
					}
					label={
						<Box display="flex">
							<Typography>
								{t("view.adminCompanies.unlimitedCatalogLicenseLabel")}
							</Typography>
							<HelpTooltip
								title={t("view.adminCompanies.unlimitedCatalogLicenseTooltip")}
								IconProps={{ style: { marginLeft: "auto" } }}
							/>
						</Box>
					}
				/>
			</FormControl>

			<FormControl fullWidth margin="normal">
				<Box display="flex" alignItems="center">
					<Box width="100%">
						<InputLabel>{t("view.admin.users")}</InputLabel>
						<Select
							multiple
							fullWidth
							disabled={!Array.isArray(users)}
							value={usersValue || []}
							onChange={onChange("users")}
							error={Boolean(error?.users)}
							renderValue={selected => (
								<Box display="flex" flexWrap="wrap" gridGap={5}>
									{selected.map(user => (
										<Chip
											key={user.id}
											label={user.fullName}
											avatar={<UserAvatar user={user} />}
										/>
									))}
								</Box>
							)}
						>
							{Object.keys(groupedUsers).length &&
								Object.keys(groupedUsers).map(renderGroupedUsers)}
						</Select>

						{!Array.isArray(users) && <SelectLoaderFormHelper />}
						{error?.users && (
							<FormHelperText error>{error.users}</FormHelperText>
						)}
					</Box>
					<HelpTooltip
						title={t("view.adminCompanies.usersTooltip")}
						IconProps={{ style: { marginTop: 13, marginLeft: 8 } }}
					/>
				</Box>
			</FormControl>

			<FormControl fullWidth margin="normal">
				<Box display="flex" alignItems="center">
					<Box width="100%">
						<InputLabel>{t("view.admin.catalogs")}</InputLabel>
						<Select
							multiple
							fullWidth
							disabled={!Array.isArray(catalogs)}
							value={catalogsValue || []}
							onChange={onChange("catalogs")}
							error={Boolean(error?.catalogs)}
							renderValue={selected => (
								<Box display="flex" flexWrap="wrap" gridGap={5}>
									{selected.map(catalog => (
										<Chip key={catalog.id} label={catalog.name} />
									))}
								</Box>
							)}
						>
							{Object.keys(groupedCatalogs).length &&
								Object.keys(groupedCatalogs).map(renderGroupedCatalogs)}
						</Select>

						{!Array.isArray(catalogs) && <SelectLoaderFormHelper />}
						{error?.catalogs && (
							<FormHelperText error>{error.catalogs}</FormHelperText>
						)}
					</Box>
					<HelpTooltip
						title={t("view.adminCompanies.catalogsTooltip")}
						IconProps={{ style: { marginTop: 13, marginLeft: 8 } }}
					/>
				</Box>
			</FormControl>
		</form>
	)
}

const UserMenuItem = ({ user }) => {
	const { fullName, role, company } = user
	const avatarSizeInPx = 25

	return (
		<Box
			display="flex"
			justifyContent="space-between"
			alignItems="center"
			width="100%"
		>
			<Box display="flex" alignItems="center" gridGap={8}>
				<UserAvatar
					user={user}
					style={{ width: avatarSizeInPx, height: avatarSizeInPx }}
				/>
				<Typography component="span">{fullName}</Typography>
			</Box>

			<Typography component="span" variant="body2" color="textSecondary">
				{company ? (
					<>
						<UserRoleChip role={role} /> at {company.name}
					</>
				) : (
					<UserRoleChip role={role} />
				)}
			</Typography>
		</Box>
	)
}

const useGroupedUsers = (users, company) => {
	const { t } = useTranslation()

	return useMemo(() => {
		const sortedUsers = sortUsers(users)
		const unassigned = sortedUsers.filter(user => !user.company)
		const sameCompany = sortedUsers.filter(
			user => user.company && user.company.id === company.id
		)
		const otherCompanies = sortedUsers.filter(
			user => user.company && user.company.id !== company.id
		)

		return {
			[t("view.adminCompanies.unassigned")]: unassigned,
			[String(company.name)]: sameCompany,
			[t("view.adminCompanies.otherCompanies")]: otherCompanies
		}
	}, [t, users, company])
}

const useGroupedCatalogs = (catalogs, company) => {
	const { t } = useTranslation()

	return useMemo(() => {
		const owned = catalogs.filter(
			catalog => catalog?.company?.id === company.id
		)
		const otherCompanies = catalogs.filter(
			catalog => catalog?.company?.id !== company.id
		)

		return {
			[String(company.name)]: owned,
			[t("view.adminCompanies.otherCompanies")]: otherCompanies
		}
	}, [t, catalogs, company])
}

const SelectLoaderFormHelper = () => {
	const { t } = useTranslation()

	return (
		<FormHelperText
			component="div"
			style={{ display: "flex", alignItems: "center", columnGap: 5 }}
		>
			<CircularProgress size={12} />
			<span style={{ position: "relative", top: 1 }}>
				{t("view.adminCompanies.loadingOptions")}
			</span>
		</FormHelperText>
	)
}

export default Form
